import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Carte : surface blanche, filet fin, ombre légère. Jamais de carte dans une carte : à l'intérieur,
 * séparer par des filets (`divide-y divide-line`) ou des groupes espacés.
 * `interactive` : survol et appui visibles, pour une carte entièrement cliquable (lien autour).
 */
export function Card({
  children,
  className,
  padding = 'md',
  interactive = false,
  as: Tag = 'div',
  ...rest
}: HTMLAttributes<HTMLElement> & {
  children: ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  interactive?: boolean;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return (
    <Tag
      className={cx(
        'rounded-2xl border border-line bg-surface',
        { none: '', sm: 'p-3', md: 'p-(--card-p)', lg: 'p-(--card-p-lg)' }[padding],
        interactive &&
          'transition-[border-color,background-color] duration-150 ease-out hover:border-line-strong hover:bg-hover active:bg-hover',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** En-tête de carte : titre, sous-titre et action à droite. */
export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('mb-2.5 flex items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h3 className="text-base font-medium text-ink">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-sm text-ink-3">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}
